'use client'

import { Modal } from '@/Modal'
import { useActionState } from 'react'
import { useAuth } from '@/Auth/useAuth'
import { useRouter } from 'next/navigation'
import { SubmitButton } from './SubmitButton'
import { Input } from '@/General/Forms/Input'
import { CloseButton } from './CloseButton'
import { ModalHeader } from './ModalHeader'
import { validateInputs } from './helpers'

export const LoginModal = ({
  isLoginOpen,
  setIsLoginOpen,
  message,
  onLoginSuccess,
  redirectTo,
  redirectToAccount = false,
  initialEmail,
}: LoginModalProps) => {
  const { signIn } = useAuth()
  const router = useRouter()
  const redirectTarget =
    redirectTo ?? (redirectToAccount ? '/account' : undefined)

  const [state, submitAction, isPending] = useActionState(
    async (_: unknown, formData: FormData) => {
      const data = {
        inputs: {
          email: formData.get('email') as string,
          password: formData.get('password') as string,
        },
      }

      const validation = validateInputs(data)
      if (!validation.success) {
        return { errors: validation.errors, data }
      }

      try {
        const success = await signIn(
          data.inputs.email,
          data.inputs.password,
          {
            context: 'header-login',
            redirectTo: redirectTarget,
          }
        )
        if (success) {
          setIsLoginOpen(false)

          if (onLoginSuccess) {
            onLoginSuccess()
          }
        }
      } catch {
        return {
          errors: [{ key: null, value: 'Login failed. Please try again.' }],
          data: {
            inputs: {
              email: data.inputs.email,
              password: '',
            },
          },
        }
      }
    },
    null
  )

  return (
    <Modal
      isOpen={isLoginOpen}
      onClose={() => setIsLoginOpen(false)}
      maxWidth="sm"
      className="sm:max-w-[460px] align-middle"
      showCloseButton={false}
    >
      <div className="relative">
        <div className="relative bg-white px-6 py-5 border-b border-gray-200">
          <CloseButton setIsLoginOpen={setIsLoginOpen} />
          <ModalHeader />
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {message && (
            <p className="text-gray-600 text-center text-sm mb-4">{message}</p>
          )}
          <form action={submitAction} className="space-y-4" noValidate>
            {state?.errors?.length != null && state.errors.length > 0 && (
              <div className="text-red-700 text-sm text-center px-3 py-2 bg-red-50 rounded-md">
                {state.errors.map(error => error.value).join(', ')}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                >
                  Email address
                </label>
                <Input
                  key={state?.data?.inputs?.email ?? initialEmail ?? 'email'}
                  defaultValue={state?.data?.inputs?.email ?? initialEmail}
                  isError={state?.errors?.some(error => error.key === 'email')}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                >
                  Password
                </label>
                <Input
                  key={state?.data?.inputs?.password ?? 'password'}
                  defaultValue={state?.data?.inputs?.password}
                  isError={state?.errors?.some(
                    error => error.key === 'password'
                  )}
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                />
              </div>
            </div>
            <div className="pt-1">
              <SubmitButton isPending={isPending} />
            </div>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setIsLoginOpen(false)
                  // Build signup URL with redirect and email if available
                  const signupParams = new URLSearchParams()
                  if (redirectTarget) {
                    signupParams.set('redirect', redirectTarget)
                  }
                  if (initialEmail) {
                    signupParams.set('email_address', initialEmail)
                  }
                  const signupUrl = `/account/signup${
                    signupParams.toString() ? `?${signupParams.toString()}` : ''
                  }`
                  router.push(signupUrl)
                }}
                className="text-vi-red hover:text-vi-red-dark font-semibold cursor-pointer"
              >
                Sign up for free
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => {
                  setIsLoginOpen(false)
                  router.push('/account/reset/')
                }}
                className="text-vi-red hover:text-vi-red-dark font-semibold cursor-pointer"
              >
                Forgot password?
              </button>
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}

interface LoginModalProps {
  isLoginOpen: boolean
  setIsLoginOpen: (open: boolean) => void
  message?: string
  onLoginSuccess?: () => void
  redirectTo?: string | null
  redirectToAccount?: boolean
  initialEmail?: string
}
